import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Layers, Settings2, Sparkles, Zap } from "lucide-react";
import { Link } from "react-router-dom";

const Hero = () => {
  const highlights = [
    { icon: Layers, text: "Core inklusive", sub: "DMS, E-Mail, Berechtigungen" },
    { icon: Zap, text: "Punkte statt starre Pakete", sub: "Flexibel kombinieren" },
    { icon: Settings2, text: "Konfigurator in 2 Min.", sub: "Sofort loslegen" },
  ];

  const scrollToModule = () => {
    const el = document.querySelector("#module");
    el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-[90vh] overflow-hidden">
      <div className="absolute inset-0 gradient-hero pattern-grid" />
      <div className="absolute right-0 top-20 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
      <div className="absolute -left-20 bottom-20 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />

      <div className="container relative z-10 pt-12 lg:pt-20">
        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-20">
          {/* Left Content */}
          <div className="flex flex-col items-start">
            {/* Badge */}
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-2">
              <Sparkles className="h-4 w-4 text-accent" />
              <span className="text-sm font-semibold text-accent">Neu: Modul-Baukasten</span>
            </div>

            {/* Headline */}
            <h1 className="mb-6 font-display text-5xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-6xl lg:text-7xl">
              Stellen Sie sich
              <br />
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  Ihre Software
                </span>
              </span>
              <br />
              zusammen.
            </h1>

            <p className="mb-8 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Wählen Sie Module wie <strong className="text-foreground">Rechnungen, CRM, Lager, Personal, Buchhaltung</strong> – passend zu Ihrem Workflow.
            </p>

            {/* Highlight cards */}
            <div className="mb-10 grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-xl">
              {highlights.map(({ icon: Icon, text, sub }) => (
                <div
                  key={text}
                  className="rounded-xl border border-border bg-card p-3 shadow-card"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                      <Icon className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="text-xs font-bold text-foreground">{text}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{sub}</p>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button
                size="lg"
                className="group relative h-14 overflow-hidden bg-primary px-8 text-base font-semibold text-primary-foreground shadow-xl shadow-primary/20 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/30"
                asChild
              >
                <Link to="/konfigurator">
                  <Settings2 className="mr-2 h-5 w-5" />
                  Konfigurator öffnen
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className="h-14 gap-2 px-6 text-base font-medium text-muted-foreground hover:text-foreground"
                onClick={scrollToModule}
              >
                Module entdecken
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Right - Software Mockup */}
          <div className="relative lg:pl-8">
            <div className="relative animate-float">
              <div className="shadow-card-hover rounded-2xl border border-border/50 bg-card p-3">
                <div className="mb-3 flex items-center gap-3 px-2">
                  <div className="flex gap-2">
                    <div className="h-3 w-3 rounded-full bg-destructive/70" />
                    <div className="h-3 w-3 rounded-full bg-accent/70" />
                    <div className="h-3 w-3 rounded-full bg-success/70" />
                  </div>
                  <div className="flex-1 rounded-lg bg-muted px-4 py-2 text-xs font-medium text-muted-foreground">
                    app.nxtinvoice.de/dashboard
                  </div>
                </div>

                <div className="aspect-[4/3] overflow-hidden rounded-xl bg-gradient-to-br from-secondary via-muted/50 to-secondary">
                  <div className="h-full w-full p-6">
                    <div className="mb-6 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg bg-primary" />
                        <div className="h-4 w-32 rounded-md bg-foreground/20" />
                      </div>
                      <div className="flex gap-2">
                        <div className="h-9 w-28 rounded-lg bg-accent shadow-lg shadow-accent/20" />
                      </div>
                    </div>

                    <div className="mb-6 grid grid-cols-3 gap-4">
                      {[
                        { color: "bg-accent/20" },
                        { color: "bg-success/20" },
                        { color: "bg-primary/20" },
                      ].map((stat, i) => (
                        <div key={i} className="rounded-xl bg-card p-4 shadow-sm">
                          <div className="mb-2 h-3 w-12 rounded bg-muted-foreground/20" />
                          <div className={`h-5 w-20 rounded-md ${stat.color}`} />
                        </div>
                      ))}
                    </div>

                    <div className="rounded-xl bg-card p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between">
                        <div className="h-4 w-28 rounded bg-foreground/20" />
                        <div className="h-7 w-20 rounded-lg bg-muted" />
                      </div>
                      <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3">
                            <div className="flex items-center gap-4">
                              <div className="h-8 w-8 rounded-lg bg-primary/10" />
                              <div className="space-y-1">
                                <div className="h-3 w-24 rounded bg-foreground/20" />
                                <div className="h-2 w-16 rounded bg-muted-foreground/20" />
                              </div>
                            </div>
                            <div className="h-6 w-16 rounded-full bg-success/20" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -bottom-6 -left-6 rounded-xl border border-border bg-card px-5 py-4 shadow-card">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                    <Layers className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Baukasten</p>
                    <p className="font-display text-sm font-bold text-foreground">18+ Module</p>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 top-1/4 rounded-xl border border-border bg-card px-4 py-3 shadow-card">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-success" />
                  <span className="text-xs font-medium text-foreground">Live Sync aktiv</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-secondary/50 to-transparent" />
    </section>
  );
};

export default Hero;
